/**
 * Ask the Readiness assistant. On an item it knows the item; on the overview it
 * knows the process. It reads the regulation, the plan's policies, the reading of
 * the final rule, the calendar and the Medicaid contract, and shows where each
 * answer comes from. It never changes anything.
 */
import React, { useEffect, useRef, useState } from 'react';
import { api, fmtWhen } from './api.js';
import { Card, Button } from './ui.jsx';

const STARTERS_ITEM = [
  'What does CMS actually require here, in plain words?',
  'Do we already have a policy that covers this?',
  'What should I send to show this is in place?',
  'What does the regulation say, word for word?',
  'When is this due, and what else is due around then?',
];
const STARTERS_PROCESS = [
  'How does Readiness work, start to finish?',
  'What happens when I release the year to owners?',
  'What is the difference between Show it, Confirm it and Read it?',
  'Which items are overdue right now?',
  'What does the Medicaid contract say about readiness for January 1?',
];

export default function Assistant({ boot, item = null, initialMessages = null }) {
  const [messages, setMessages] = useState(initialMessages || []); // initialMessages: the render harness only
  const [conversationId, setConversationId] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState({});
  const bottom = useRef(null);

  useEffect(() => {
    if (initialMessages) return undefined;
    let alive = true;
    api(`/assistant/history${item ? `?item_id=${encodeURIComponent(item.id)}` : ''}`).then((d) => { if (alive && d?.success) { setMessages(d.messages || []); setConversationId(d.conversation_id || null); } });
    return () => { alive = false; };
  }, [item, initialMessages]);
  useEffect(() => { bottom.current?.scrollIntoView?.({ block: 'nearest' }); }, [messages.length, busy]);

  const ask = async (question) => {
    const text = String(question || q).trim(); if (!text || busy) return;
    setBusy(true); setErr(null); setQ('');
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() }]);
    const d = await api('/assistant/ask', { method: 'POST', body: { question: text, item_id: item?.id || null, conversation_id: conversationId } });
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'The assistant could not answer'); return; }
    setConversationId(d.conversation_id);
    setMessages((m) => [...m, { id: `a-${Date.now()}`, role: 'assistant', content: d.answer, sources: d.sources, next_steps: d.next_steps, created_at: new Date().toISOString() }]);
  };
  const starters = item ? STARTERS_ITEM : STARTERS_PROCESS;
  const newChat = () => { setConversationId(null); setMessages([]); setErr(null); };

  return (
    <Card title={item ? 'Ask about this item' : 'Ask the Readiness assistant'} tone="border-orange-200" right={<div className="flex items-center gap-3"><span className="text-xs text-slate-500">reads the regulation, our policies, the final rule, the calendar</span>{messages.length > 0 && <button type="button" className="text-xs text-slate-500 underline" onClick={newChat}>New conversation</button>}</div>}>
      {messages.length === 0 && !busy && (
        <div className="flex flex-wrap gap-2 mb-3">
          {starters.map((s) => <button key={s} type="button" onClick={() => ask(s)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:border-orange-300 hover:text-orange-800">{s}</button>)}
        </div>
      )}
      <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
        {messages.map((m, i) => m.role === 'user' ? (
          <div key={m.id || i} className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-sm bg-slate-100 px-3.5 py-2 text-sm text-slate-800">{m.content}</div></div>
        ) : (
          <div key={m.id || i} className="flex justify-start">
            <div className="max-w-[92%] rounded-2xl rounded-bl-sm bg-orange-50/60 border border-orange-100 px-3.5 py-2.5 text-sm text-slate-800">
              <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
              {Array.isArray(m.next_steps) && m.next_steps.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[13px] text-slate-700">{m.next_steps.map((s, k) => <li key={k} className="flex gap-2"><span className="text-orange-600">→</span><span>{s}</span></li>)}</ul>
              )}
              {Array.isArray(m.sources) && m.sources.length > 0 && (
                <div className="mt-2">
                  <button type="button" className="text-[11px] font-semibold text-orange-800 underline" onClick={() => setOpen((o) => ({ ...o, [m.id || i]: !o[m.id || i] }))}>{open[m.id || i] ? 'Hide sources' : `Where this comes from (${m.sources.length})`}</button>
                  {open[m.id || i] && (
                    <ul className="mt-1.5 space-y-1.5">{m.sources.map((s, k) => <li key={k} className="text-[12px] text-slate-700 border-l-2 border-orange-300 pl-2"><span className="font-semibold">{s.label || s.ref}</span>: "{s.quote}"</li>)}</ul>
                  )}
                </div>
              )}
              {m.created_at && <div className="text-[10px] text-slate-400 mt-1">{fmtWhen(m.created_at)}</div>}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="rounded-2xl rounded-bl-sm bg-orange-50/60 border border-orange-100 px-3.5 py-2.5 text-sm text-slate-500"><span className="inline-block w-2 h-2 rounded-full bg-orange-500 animate-pulse mr-2" />Reading the sources…</div></div>}
        <div ref={bottom} />
      </div>
      <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); ask(); }}>
        <input className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200" value={q} onChange={(e) => setQ(e.target.value)} placeholder={item ? `Ask about ${item.cms_ref}…` : 'Ask how Readiness works, or about any item…'} disabled={busy} />
        <Button type="submit" disabled={busy || !q.trim()}>Ask</Button>
      </form>
      {err && <div className="text-xs text-rose-600 mt-1">{err}</div>}
      <div className="text-[11px] text-slate-500 mt-2">Answers point to their sources. The assistant does not decide or change anything; compliance does. Do not include member information.</div>
    </Card>
  );
}
